// File: src/services/safetyCalendarService.js
// Client wrapper for the Safety Calendar API. Mirrors the platform's service
// pattern (Bearer token from localStorage, JSON, handleResponse).

const API_URL = '/api/safety-calendar';

const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    throw data || { message: `Request failed with status ${response.status}` };
  }
  return data;
};

export const getEntries = async (month) =>
  handleResponse(await fetch(`${API_URL}/entries?month=${encodeURIComponent(month)}`, {
    method: 'GET', headers: getHeaders(),
  }));

export const getLatestOsha = async () =>
  handleResponse(await fetch(`${API_URL}/latest-osha`, { method: 'GET', headers: getHeaders() }));

export const getSettings = async () =>
  handleResponse(await fetch(`${API_URL}/settings`, { method: 'GET', headers: getHeaders() }));

export const createEntry = async (payload) =>
  handleResponse(await fetch(`${API_URL}/entries`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(payload),
  }));

export const updateEntry = async (id, payload) =>
  handleResponse(await fetch(`${API_URL}/entries/${id}`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify(payload),
  }));

export const deleteEntry = async (id) =>
  handleResponse(await fetch(`${API_URL}/entries/${id}`, { method: 'DELETE', headers: getHeaders() }));

export const setOshaBaseline = async (date) =>
  handleResponse(await fetch(`${API_URL}/settings/osha-baseline`, {
    method: 'PUT', headers: getHeaders(), body: JSON.stringify({ date }),
  }));

export const clearOshaBaseline = async () =>
  handleResponse(await fetch(`${API_URL}/settings/osha-baseline`, {
    method: 'DELETE', headers: getHeaders(),
  }));

// Fetch the CSV with the auth header, then trigger a browser download.
export const exportCsv = async (scope, month) => {
  const qs = new URLSearchParams({ scope });
  if (scope === 'month' && month) qs.set('month', month);
  const response = await fetch(`${API_URL}/export?${qs.toString()}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
  });
  if (!response.ok) {
    let message = `Export failed with status ${response.status}`;
    try { const d = await response.json(); message = d.message || message; } catch (e) { /* not json */ }
    throw new Error(message);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `safety_calendar_${scope}.csv`;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export default {
  getEntries, getLatestOsha, getSettings, createEntry, updateEntry,
  deleteEntry, setOshaBaseline, clearOshaBaseline, exportCsv,
};
